/**
 * Manually-maintained analytics event types.
 *
 * These events are NOT emitted by generate-event-types (see
 * packages/client-analytics/scripts/event-generator.ts) — that tool only
 * turns namespaced JSON schemas from the general-event-spec repo's
 * web-client schema directories into `generated/global.ts`, and it fully
 * overwrites that file on every run. Generic/system-level events (like
 * `system.oauth_view_start` / `system.oauth_view_end`) are not currently
 * picked up by the tool, so they are hand-declared here instead of in
 * `generated/global.ts`.
 *
 * Once the generator (or its upstream schemas) picks these events up,
 * delete this file and rely on `generated/global.ts` alone.
 */

/* eslint-disable -- manually-maintained; lint findings live with upstream schema authors */

/**
 * Fired when the third-party OAuth provider view is dismissed, regardless of outcome.
 */
export interface ISystemOauthViewEnd {
	type: 'system.oauth_view_end';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		/**
		 * The OAuth provider whose authentication view was dismissed
		 */
		provider: 'google' | 'apple';
		/**
		 * Outcome of the OAuth view interaction
		 */
		status: 'success' | 'failure' | 'cancel';
		/**
		 * Additional detail about the failure. Present only when status is 'failure'.
		 */
		message?: string;
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

/**
 * Fired when the third-party OAuth provider view is presented to the user.
 */
export interface ISystemOauthViewStart {
	type: 'system.oauth_view_start';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		/**
		 * The OAuth provider whose authentication view was launched
		 */
		provider: 'google' | 'apple';
		/**
		 * The context from which the OAuth view was launched, e.g. login, signup
		 */
		launch_context?: string;
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

export const MANUAL_EVENT_TYPES = {
	SYSTEM_OAUTH_VIEW_END: 'system.oauth_view_end',
	SYSTEM_OAUTH_VIEW_START: 'system.oauth_view_start',
} as const;

type ManualKnownEventInterface = ISystemOauthViewEnd | ISystemOauthViewStart;

export type ManualKnownEvents = {
	[E in ManualKnownEventInterface as E['type']]: E;
};
